import {
  AfterContentInit, AfterViewChecked,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  SimpleChanges
} from '@angular/core';
import {Event} from "@angular/router";
import {Observable, throwError} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {ApiUrl} from "../../shared/models/apiurl.models";
import {coerceStringArray} from "@angular/cdk/coercion";
import {AdvFormService} from "../../pages/create-adv/services/adv-form.service";
import {MatDialog} from "@angular/material/dialog";
import {
  PhoneValidationDialogComponent,
  PhoneValidationResult
} from "./phone-validation-dialog/phone-validation-dialog.component";
import {TermsConsentDialogComponent} from "./terms-consent-dialog/terms-consent-dialog.component";
import {ConsentService, TermsAcceptanceInfo} from "../../services/consent.service";

export interface PersonContact{
  userId: number,
  contactPerson: string,
  phoneNumber: string,
  email: string,
  acceptTerms: boolean,
  acceptAdv: boolean,
  validPhone:boolean
}

@Component({
    selector: 'app-person-contact',
    templateUrl: './person-contact.component.html',
    styleUrls: ['./person-contact.component.css'],
    standalone: false
})
export class PersonContactComponent implements OnInit {
  personContact: PersonContact;
  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  }

  /** SCRUM-168: the terms checkbox unlocks only after the תקנון was read to the end. */
  termsRead = false;
  /** SCRUM-169: server already holds an acceptance of the CURRENT terms version. */
  termsAcceptedOnServer = false;
  termsAcceptance: TermsAcceptanceInfo | null = null;
  consentError = '';
  private termsDialogOpen = false;
  /** The OTP dialog applies its own result; afterAllClosed must not reload over it. */
  private phoneDialogOpen = false;

  constructor(private http: HttpClient, private apiKey:ApiUrl, private formService: AdvFormService, public dialog: MatDialog,
              private consentService: ConsentService) {
    // this.loadData()
    this.dialog.afterAllClosed.subscribe(res=>{
      // The terms dialog handles its own result; reloading here would race
      // with the consent POST and could untick the box.
      if (this.termsDialogOpen) {
        this.termsDialogOpen = false;
        return;
      }
      if (this.phoneDialogOpen) {
        return;
      }
      this.loadData()
    })
    this.personContact = {
      acceptAdv: false,
      acceptTerms: false,
      contactPerson: "",
      email: "",
      validPhone:false,
      phoneNumber: "",
      userId: -1
    };
  }

  isUserValid: boolean = false;
  /** The number the server verified; editing the phone away from it un-verifies. */
  private verifiedPhone = '';
  set isUserValidSetter(isuservalid: boolean){
    this.isUserValid = isuservalid
    this.verifiedPhone = isuservalid ? this.phoneDigits(this.personContact?.phoneNumber) : '';
  }
  /** "מספר מאומת" only for a filled-in number that is the verified one. */
  get isUserValidGetter(){
    const digits = this.phoneDigits(this.personContact?.phoneNumber);
    return this.isUserValid && digits.length >= 9 && digits === this.verifiedPhone;
  }

  private phoneDigits(value: any): string {
    // Compare numbers by their last 9 digits so "054-1234567" and the
    // server's "+972541234567" are the same number.
    return String(value ?? '').replace(/\D/g, '').slice(-9);
  }

  loadData(): void{
    // Keep what the user already ticked on this page; the server can only add.
    const localTerms = !!this.personContact?.acceptTerms || this.formService.termsAcceptedLocally;
    const localAdv = !!this.personContact?.acceptAdv;
    // Never blank a number the user typed / just verified because the account
    // row has no mainPhone yet.
    const localPhone = this.personContact?.phoneNumber || '';
    const localVerified = this.isUserValidGetter;
    this.getUserValidation().subscribe((res)=>{
      const serverPhone = res?.phoneNumber || res?.user?.mainPhone || '';
      const phone = localPhone || this.displayPhone(serverPhone);
      const serverVerified = !!res.validPhone && !!serverPhone
        && this.phoneDigits(serverPhone) === this.phoneDigits(phone);
      this.personContact = {
        acceptAdv: !!res.acceptAdv || localAdv,
        acceptTerms: !!res.acceptTerms || localTerms,
        contactPerson: this.personContact?.contactPerson || res.user?.first_name || '',
        email: res.user?.email,
        phoneNumber: phone,
        userId: res.user?.id ?? res.id,
        validPhone: serverVerified || localVerified
      };
      this.applyServerTerms(!!res.acceptTerms, res.termsAcceptance || null);
      this.isUserValidSetter = serverVerified || localVerified
      this.MissingData()
    },error => this.getUserInfo().subscribe(res=>{
      this.personContact = {
        acceptAdv: localAdv,
        acceptTerms: localTerms,
        contactPerson: this.personContact?.contactPerson || res.first_name,
        email: res.email,
        phoneNumber: localPhone || this.displayPhone(res.mainPhone),
        userId: res.id,
        validPhone: localVerified
      };
      this.isUserValidSetter = localVerified
      this.MissingData()
      this.loadConsentStatus();
    }));
  }

  /** Logged-in user without a phone-validation row: read consent directly. */
  private loadConsentStatus(): void {
    this.consentService.getStatus().subscribe({
      next: status => {
        this.personContact.acceptTerms = this.personContact.acceptTerms || status.acceptTerms;
        this.personContact.acceptAdv = this.personContact.acceptAdv || status.acceptAdv;
        this.applyServerTerms(status.acceptTerms, status.termsAcceptance);
        this.MissingData();
      },
      error: () => {}
    });
  }

  /** "+972541234567" -> "0541234567" for the input. */
  private displayPhone(value: any): string {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (!digits) return '';
    return digits.startsWith('972') ? '0' + digits.slice(3) : digits;
  }

  private applyServerTerms(accepted: boolean, acceptance: TermsAcceptanceInfo | null): void {
    this.termsAcceptedOnServer = accepted;
    this.termsAcceptance = acceptance;
    if (accepted) {
      this.termsRead = true;
      this.personContact.acceptTerms = true;
      // Already approved on the server: the plan step must not ask again.
      this.formService.termsAcceptedLocally = true;
    }
  }

  private get isLoggedIn(): boolean {
    return !!this.personContact && this.personContact.userId > 0;
  }

  /** SCRUM-168: "התקנון" link / clicking the locked checkbox opens the terms. */
  openTerms(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.termsDialogOpen = true;
    this.dialog.open(TermsConsentDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100vh - 24px)',
      direction: 'rtl',
      autoFocus: false
    }).afterClosed().subscribe((accepted: boolean | undefined) => {
      if (accepted) {
        this.termsRead = true;
        this.setTerms(true);
      }
    });
  }

  onTermsOptionClick(event: MouseEvent): void {
    if (this.termsAcceptedOnServer) {
      event.preventDefault();
      return;
    }
    if (!this.termsRead && !this.personContact.acceptTerms) {
      this.openTerms(event);
    }
  }

  onTermsChange(checked: boolean): void {
    this.setTerms(checked);
  }

  private setTerms(checked: boolean): void {
    this.consentError = '';
    this.personContact.acceptTerms = checked;
    this.formService.termsAcceptedLocally = checked;
    if (!checked) {
      // SCRUM-122: no marketing consent without the terms.
      this.personContact.acceptAdv = false;
    }
    this.MissingData();
    if (checked && this.isLoggedIn && !this.termsAcceptedOnServer) {
      this.consentService.update({acceptTerms: true}).subscribe({
        next: status => this.applyServerTerms(status.acceptTerms, status.termsAcceptance),
        error: () => this.consentError = 'לא הצלחנו לשמור את אישור התקנון. נסו שוב.'
      });
    }
  }

  /** SCRUM-168: marketing is optional, opt-in, and only after the terms. */
  onAdvChange(checked: boolean): void {
    this.consentError = '';
    if (checked && !this.personContact.acceptTerms) {
      this.personContact.acceptAdv = false;
      return;
    }
    this.personContact.acceptAdv = checked;
    this.MissingData();
    if (this.isLoggedIn && this.termsAcceptedOnServer) {
      this.consentService.update({acceptAdv: checked}).subscribe({
        error: () => this.consentError = 'לא הצלחנו לשמור את בחירת הדיוור. נסו שוב.'
      });
    }
  }

  private handleObserverError(error: any): Observable<any>{
    return throwError(error.error || error)
  }

  getUserValidation(): Observable<any>{
    return this.http.get(this.apiKey.getPhoneValidationUrl()+"validate/", this.options)
      .pipe(map((res) => res ? res : null ),catchError(this.handleObserverError)
      );
  }

  getUserInfo(): Observable<any>{
    return this.http.get(this.apiKey.getUserInfo(), this.options)
      .pipe(map((res) => res ? res : null ),catchError(this.handleObserverError)
      );
  }
  MissingData(): void{
    // Never drop a terms approval made elsewhere in this session (SCRUM-239).
    if (this.formService.termsAcceptedLocally && this.personContact) {
      this.personContact.acceptTerms = true;
    }
    this.formService.addQuestion('userContactDetails',this.personContact);
  }

  onChange($event: any): void{
    // A changed / cleared phone must be verified again before publishing.
    const verified = this.isUserValidGetter;
    if (this.personContact) this.personContact.validPhone = verified;
    this.MissingData()
  }
  /**
   * Back on the contact step: show what the user already entered (name,
   * phone, consents, verification) instead of an empty form; loadData() then
   * only adds what the server knows.
   */
  ngOnInit(): void {
    const saved = this.formService.getQuestion('userContactDetails');
    if (saved && typeof saved === 'object') {
      this.personContact = {...this.personContact, ...saved};
      this.isUserValidSetter = saved.validPhone === true;
    }
  }

  validatePhone() {
    this.consentError = '';
    const digits = String(this.personContact?.phoneNumber ?? '').replace(/\D/g, '');
    if (digits.length < 9) {
      this.consentError = 'יש להזין מספר טלפון תקין לפני האימות.';
      return;
    }
    // The dialog sends the code itself (and handles resend / cooldown / limits).
    this.phoneDialogOpen = true;
    this.dialog.open(PhoneValidationDialogComponent, {
      data: {contact: {...this.personContact}, termsLocked: this.termsAcceptedOnServer},
      width: '480px',
      maxWidth: 'calc(100vw - 24px)',
      direction: 'rtl',
      autoFocus: false,
      panelClass: 'otp-dialog-panel'
    }).afterClosed().subscribe((result: PhoneValidationResult | undefined) => {
      this.phoneDialogOpen = false;
      if (result?.linkedToOtherAccount) {
        this.isUserValidSetter = false;
        this.personContact.validPhone = false;
        this.consentError = result.message || 'מספר הטלפון הזה מקושר לחשבון אחר. יש להתחבר לחשבון הנכון.';
        this.MissingData();
        return;
      }
      if (result?.verified) {
        this.personContact.validPhone = true;
        if (result.acceptTerms && !this.personContact.acceptTerms) {
          this.termsRead = true;
          this.setTerms(true);
        }
        this.personContact.acceptAdv = !!result.acceptAdv && this.personContact.acceptTerms;
        this.isUserValidSetter = true;
        this.MissingData();
      }
      this.loadData();
    });
  }



}
